import { DrawingPoint } from './types';
import { applySmoothing, calculateSpeed, adjustBrushSizeForSpeed } from './SmoothingUtils';

class DrawingCanvas {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private isDrawing: boolean = false;
  private lastPoint: DrawingPoint | null = null;
  private lastTime: number | null = null; // For speed calculation
  private currentColor: string = '#FF6B6B';
  private baseSize: number = 5; // Base size before speed adjustment
  private currentSize: number = 5;
  private currentTool: 'brush' | 'eraser' | 'marker' | 'spray' = 'brush';
  private smoothingEnabled: boolean = true;
  private speedAdjustmentEnabled: boolean = true;
  private trailEnabled: boolean = true;
  
  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d')!;
    
    // Set initial canvas properties
    this.context.lineCap = 'round';
    this.context.lineJoin = 'round';
    this.context.imageSmoothingEnabled = true;
  }

  public setDimensions(width: number, height: number) {
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
      // Reset context properties that get lost on resize
      this.context.lineCap = 'round';
      this.context.lineJoin = 'round';
      this.context.imageSmoothingEnabled = true;
    }
  }
  
  private resizeCanvas() {
    // Set canvas dimensions to match its display size
    const displayWidth = this.canvas.clientWidth;
    const displayHeight = this.canvas.clientHeight;
    
    if (this.canvas.width !== displayWidth || this.canvas.height !== displayHeight) {
      this.canvas.width = displayWidth;
      this.canvas.height = displayHeight;
    }
  }
  
  public setDrawingState(isDrawing: boolean) {
    this.isDrawing = isDrawing;
    if (!isDrawing) {
      this.lastPoint = null;
      this.lastTime = null;
    }
  }
  
  public setCurrentTool(tool: 'brush' | 'eraser' | 'marker' | 'spray') {
    this.currentTool = tool;
  }
  
  public setCurrentColor(color: string) {
    this.currentColor = color;
  }
  
  public setCurrentSize(size: number) {
    this.baseSize = size;
    this.currentSize = size;
  }
  
  public setSmoothingEnabled(enabled: boolean) {
    this.smoothingEnabled = enabled;
  }
  
  public setSpeedAdjustmentEnabled(enabled: boolean) {
    this.speedAdjustmentEnabled = enabled;
  }
  
  public drawAt(x: number, y: number) {
    if (!this.isDrawing) {
      this.lastPoint = { x, y };
      this.lastTime = performance.now();
      return;
    }
    
    if (this.lastPoint) {
      // Apply smoothing if enabled
      let currentPoint: DrawingPoint = { x, y };
      if (this.smoothingEnabled) {
        currentPoint = applySmoothing(currentPoint, this.lastPoint);
      }
      
      // Calculate speed if needed for size adjustment
      const currentTime = performance.now();
      let adjustedSize = this.baseSize;
      
      if (this.speedAdjustmentEnabled && this.lastTime !== null) {
        const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
        const speed = calculateSpeed(currentPoint, this.lastPoint, deltaTime);
        adjustedSize = adjustBrushSizeForSpeed(this.baseSize, speed);
      }
      
      this.currentSize = adjustedSize;
      
      // Draw the line segment
      this.context.beginPath();
      this.context.moveTo(this.lastPoint.x, this.lastPoint.y);
      this.context.lineTo(currentPoint.x, currentPoint.y);
      
      if (this.currentTool === 'eraser') {
        this.context.globalCompositeOperation = 'destination-out';
        this.context.lineWidth = Math.max(8, this.baseSize * 2); // Eraser size relative to slider
      } else if (this.currentTool === 'marker') {
        this.context.globalCompositeOperation = 'source-over';
        this.context.strokeStyle = this.currentColor;
        this.context.lineWidth = adjustedSize * 1.8; // thicker
        this.context.lineCap = 'round';
        this.context.globalAlpha = 0.9;
      } else if (this.currentTool === 'spray') {
        this.context.globalCompositeOperation = 'source-over';
        this.context.strokeStyle = this.currentColor;
        this.context.lineWidth = Math.max(1, adjustedSize * 0.6);
        this.context.globalAlpha = 0.6;
        // spray effect via dots around the point
        const radius = Math.max(6, this.baseSize * 1.2);
        for (let i = 0; i < 20; i++) {
          const angle = Math.random() * Math.PI * 2;
          const r = Math.random() * radius;
          const sx = currentPoint.x + Math.cos(angle) * r;
          const sy = currentPoint.y + Math.sin(angle) * r;
          this.context.beginPath();
          this.context.arc(sx, sy, 1 + Math.random() * 1.5, 0, Math.PI * 2);
          this.context.fillStyle = this.currentColor;
          this.context.fill();
        }
      } else {
        this.context.globalCompositeOperation = 'source-over';
        this.context.strokeStyle = this.currentColor;
        this.context.lineWidth = adjustedSize; // respects slider with speed factor
      }
      
      this.context.stroke();

      // Optional trailing effect for visual smoothness
      if (this.trailEnabled && this.currentTool !== 'eraser') {
        this.context.save();
        this.context.globalCompositeOperation = 'source-over';
        const grad = this.context.createRadialGradient(currentPoint.x, currentPoint.y, 0, currentPoint.x, currentPoint.y, Math.max(10, this.currentSize * 2));
        grad.addColorStop(0, this.currentColor + '80');
        grad.addColorStop(1, this.currentColor + '00');
        this.context.fillStyle = grad as any;
        this.context.beginPath();
        this.context.arc(currentPoint.x, currentPoint.y, Math.max(10, this.currentSize * 1.2), 0, Math.PI * 2);
        this.context.fill();
        this.context.restore();
      }
    }
    
    this.lastPoint = { x, y };
    this.lastTime = performance.now();
  }
  
  public clear() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.lastPoint = null;
    this.lastTime = null;
  }
  
  public undo() {
    // For a simple implementation, we'll just clear the canvas
    // In a more complex implementation, we would maintain a history stack
    this.clear();
  }

  public eraseCircleAt(x: number, y: number, radius: number) {
    const r = Math.max(4, radius);
    this.context.save();
    this.context.globalCompositeOperation = 'destination-out';
    this.context.beginPath();
    this.context.arc(x, y, r, 0, Math.PI * 2);
    this.context.fill();
    this.context.restore();
  }
  
  public getCanvas() {
    return this.canvas;
  }
  
  public getContext() {
    return this.context;
  }
}

export default DrawingCanvas;